import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import windowService from '../../../services/windowService';
import { handleError } from '../../../utils/apiUtils';
import useQueryParams from '../../../hooks/useQueryParams';
import { useOAuth } from './useOAuth';
import { validateGroupEasyLink } from '../api/validateGroupEasyLink';
import cacheableRequest from '../../../services/cacheableRequest';
import { AxiosResponse } from 'axios';
import { UserResponse } from '../type/userResponse';
import { saveState } from '../utils/saveUserState';
import { getRedirectURLLogin } from '../api/getRedirectURLLogin';
import { getRedirectURLRegister } from '../api/getRedirectURLRegister';
import WhiteLabel from '../../../filters/WhiteLabel';
import { forgetJwt, getJwt, keepJwt, validateJwt } from '../../../services/jwt';

const getOriginBranding = () => {
  return {
    logoSrc: WhiteLabel.logoSrc,
    alt: WhiteLabel.name,
  };
};

type LoginScenario = {
  path: string;
  title: string;
  subtitle: string;
};

const loginScenarios = [
  {
    path: 'redeem-invite',
    title: "You're on the right track!",
    subtitle: 'Log in to redeem your Easy Link.',
  },
  {
    path: 'easy-link',
    title: "You're on the right track!",
    subtitle: 'Log in or Register to join your organization.',
  },
  {
    path: 'add-to-your-training',
    title: "You're on the right track!",
    subtitle: 'Log in or Register to access your content.',
  },
  {
    path: 'checkout',
    title: "You're Almost There!",
    subtitle: 'Log in or Register to complete the checkout process.',
  },
];

function getLoginScenario(hash: string): LoginScenario | undefined {
  return loginScenarios.find(scenario => hash.includes(scenario.path));
}

export const useLogin = (checkExistingSession: boolean = true) => {
  const { loginSuccess } = useOAuth();
  const [loginMessage, setLoginMessage] = useState<{
    message?: LoginScenario;
    invalidEasyLink?: boolean;
    isLoading: boolean;
  }>();

  const [checkingSession, setCheckingSession] = useState(false);
  const query = useQueryParams({ preserveParams: true });
  const location = useLocation();
  const userManager = window.getUserManager?.();
  const isSSO = window.getIsSSO();

  const checkForExistingSession = () => {
    const token = getJwt();

    if (token) {
      return cacheableRequest(`/v1/security`).then(({ headers, data }: AxiosResponse<UserResponse>) => {
        const headerToken = headers['set-authorization'];

        // Validate it just in case the server would have send something fishy
        if (headerToken) {
          if (validateJwt(headerToken)) {
            keepJwt(headerToken);
            loginSuccess(data);
          } else {
            forgetJwt();
            console.error('Something is wrong with the jwt');
          }
        } else {
          forgetJwt();
          console.error('No Authorization header');
        }
      });
    } else {
      return Promise.resolve();
    }
  };

  const redirectToLogin = (redirectURL?: string, loginHint?: string) => {
    const searchParams = new URLSearchParams(location.search);
    const routeParam = searchParams.get('route');
    const redirectUrlAfterAuth =
      redirectURL ||
      (routeParam
        ? `/login?route=${encodeURIComponent(routeParam)}`
        : sessionStorage.getItem('redirectUrl') || undefined);

    if (isSSO) {
      saveState(redirectUrlAfterAuth);
      if (userManager) {
        userManager
          .signinRedirect({
            extraQueryParams: { login_hint: loginHint || '' },
          })
          .then(() => {
            userManager.clearStaleState();
          })
          .catch(error => {
            handleError(error as Error);
          });
      } else {
        handleError(new Error('User manager not found'));
      }
    } else {
      getRedirectURLLogin(redirectUrlAfterAuth, loginHint).then(result => {
        windowService.openUrlInSameTab(result);
      });
    }
  };

  const redirectToRegister = (redirectURL?: string) => {
    const currentUrl = redirectURL || window.location.href;

    try {
      if (isSSO) {
        saveState(currentUrl);
        if (userManager) {
          userManager
            .signinRedirect({
              extraQueryParams: {
                auth_method: 'register',
              },
            })
            .then(() => {
              userManager.clearStaleState();
            });
        } else {
          handleError(new Error('User manager not found'));
        }
      } else {
        getRedirectURLRegister(currentUrl).then(result => {
          windowService.openUrlInSameTab(result);
        });
      }
    } catch (error) {
      handleError(error as Error);
    }
  };

  const logoutUser = (): Promise<void> => {
    if (userManager) {
      return userManager.signoutRedirect();
    } else {
      return Promise.reject(new Error('User manager not found'));
    }
  };

  useEffect(() => {
    if (!checkExistingSession) {
      return;
    }

    const hash = window.location.hash;
    const activeLink = getLoginScenario(hash);
    const bypass = localStorage.getItem('bypass');

    if (activeLink) {
      if (activeLink.path === 'easy-link') {
        const easyLinkMatch = hash.split('easy-link')[1];

        const parts = decodeURIComponent(easyLinkMatch).split('/');
        const [, prefix, suffix] = parts;

        if (prefix && suffix && prefix.length === 3 && suffix.length === 4) {
          setLoginMessage({
            isLoading: true,
          });

          validateGroupEasyLink(prefix, suffix)
            .then(isValid => {
              if (!isValid) {
                setLoginMessage({
                  message: activeLink,
                  invalidEasyLink: true,
                  isLoading: false,
                });
              } else {
                setLoginMessage({
                  message: activeLink,
                  invalidEasyLink: false,
                  isLoading: false,
                });
              }
            })
            .catch(() => {
              setLoginMessage({
                invalidEasyLink: true,
                isLoading: false,
              });
            });
        }
      } else {
        setLoginMessage({
          message: activeLink,
          isLoading: false,
        });
      }
    }

    if (!bypass && query.bypass) {
      redirectToLogin();
    } else {
      setCheckingSession(true);
      checkForExistingSession()
        .then(() => {
          setCheckingSession(false);
        })
        .catch(() => {
          setCheckingSession(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.bypass]);

  return {
    checkingSession,
    loginMessage,
    getOriginBranding,
    redirectToLogin,
    redirectToRegister,
    getLoginScenario,
    logoutUser,
  };
};
